import { useEffect, useRef } from "react";
import {
  DocumentRealtimeEvent,
  RealtimeEvent,
  subscribeToDocumentRealtime,
} from "../api/realtime";
import { playDocumentEventSound } from "../utils/notificationSound";

export function useDocumentRealtimeInvalidation(
  accessToken: string | null,
  onInvalidate: (event: DocumentRealtimeEvent) => void | Promise<void>,
  shouldInvalidate?: (event: DocumentRealtimeEvent) => boolean,
  delayMs = 200,
): void {
  const onInvalidateRef = useRef(onInvalidate);
  const shouldInvalidateRef = useRef(shouldInvalidate);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEventRef = useRef<DocumentRealtimeEvent | null>(null);

  useEffect(() => {
    onInvalidateRef.current = onInvalidate;
  }, [onInvalidate]);

  useEffect(() => {
    shouldInvalidateRef.current = shouldInvalidate;
  }, [shouldInvalidate]);

  useEffect(() => {
    if (!accessToken) return;

    const unsubscribe = subscribeToDocumentRealtime(accessToken, (event: RealtimeEvent) => {
      if (event.type !== "document.changed") return;
      if (shouldInvalidateRef.current && !shouldInvalidateRef.current(event)) return;

      playDocumentEventSound(event.action, event.status, event.documentId);

      lastEventRef.current = event;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const latestEvent = lastEventRef.current;
        if (latestEvent) void onInvalidateRef.current(latestEvent);
      }, delayMs);
    });

    return () => {
      unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      lastEventRef.current = null;
    };
  }, [accessToken, delayMs]);
}
